import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import {
  A11y,
  Autoplay,
  EffectCoverflow,
  EffectFade,
  HashNavigation,
  Navigation,
  Pagination as SwiperNativePaginationModule,
} from 'swiper/modules';

import { GalleryPaginationIndicators } from './pagination/GalleryPaginationIndicators';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';

import './Gallery.scss';
import { CoverPage } from '@moment/components/CoverPage/CoverPage.tsx';
import { EnhancedGalleryItem, GalleryItem, } from '@moment/components/Gallery/types/GalleryItem.ts';
import { useGalleryPagination } from './hooks/useGalleryPagination';
import PlayIcon from './play.svg?react';
import PauseIcon from './pause.svg?react';
import BackIcon from './arrow-back.svg?react';
import { useTheme } from '@moment/hooks/useTheme.ts';
import RecirculationPage from '@moment/components/RecirculationPage/RecirculationPage';
import { useGalleryTapNavigation } from '@moment/components/Gallery/hooks/useGalleryTapNavigation.ts';
import { useTranslation } from 'react-i18next';
import { useBridge } from '@moment/bridge/bridge-provider.tsx';
import { BridgeEvent } from '@moment/bridge/client-bridge-defs.ts';
import { AdSourceType } from '@pages/Root/ContextWrapper/WithInterest/WithCommonPageData.tsx';
import { getSSRTranslation } from '@/i18n.ts';
import { useLocation, useSearchParams } from 'react-router';
import { useFotoscapeNavigate } from '@hooks/navigation.ts';
import { OwnedBy } from '@moment/net/softbox-types.ts';
import { trackInterest } from '@moment/local-personalization/scorer.ts';

export interface GalleryProps {
  items: EnhancedGalleryItem[];
  initialIndex?: number;
  autoSwipeDelay?: number;
  loop?: boolean;
  showArrows?: boolean;
  showPagination?: boolean;
  paginationType?:
    | 'bullets'
    | 'fraction'
    | 'progressbar'
    | 'customWithProgress';
  enableHashNavigation?: boolean;
  enableAutoSwipe?: boolean;
  onPageChange?: (
    index: number,
    currentItem: GalleryItem | { type: 'recirculation' } | undefined
  ) => void;
  onPlayPauseClick?: (action: 'play' | 'pause') => void;
  className?: string;
  adType?: AdSourceType;
  onSlideScrolled?: () => void;
  onSwipeDown?: () => void;
  lookbookId?: string;
  lookbookOwnedBy?: OwnedBy;
  interests: string[];
}

export const Gallery: React.FC<GalleryProps> = ({
  items = [],
  initialIndex = 0,
  autoSwipeDelay = 10000,
  loop = false,
  showArrows = true,
  showPagination = true,
  paginationType = 'customWithProgress',
  enableHashNavigation = true,
  enableAutoSwipe = true,
  onPageChange,
  className = '',
  adType = 'gam',
  onPlayPauseClick,
  onSlideScrolled,
  onSwipeDown,
  lookbookId,
  lookbookOwnedBy,
  interests = [],
}) => {
  const { theme } = useTheme();
  const { i18n, t } = useTranslation();
  const language = i18n.language;
  const bridge = useBridge();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useFotoscapeNavigate();
  const showBackButton = !!location.state?.fromApp;

  const swiperRef = useRef<SwiperInstance | null>(null);
  const [isSwiperReady, setIsSwiperReady] = useState(false);
  const [isSsr, setIsSsr] = useState(true);
  const [internalCurrentIndex, setInternalCurrentIndex] =
    useState(initialIndex);

  // New state for navigation button visibility
  const [showNavigationButtons, setShowNavigationButtons] = useState(true);
  const navigationHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Swipe detection state
  const touchStartY = useRef<number>(0);
  const touchStartX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const isSwiping = useRef<boolean>(false);
  const mountTime = useRef<number>(Date.now());

  const currentItem = items[internalCurrentIndex];
  const isSpringboard = currentItem?.lbtype === 'springboard';
  const shouldEnableAutoSwipe = enableAutoSwipe && !isSpringboard;

  const {
    activeSlideAutoplayProgress,
    isAutoplayPausedVisual,
    resetAutoplayProgressForNewSlide,
    handleSwiperAutoplayStart,
    handleSwiperAutoplayStop,
    handleSwiperAutoplayPause,
    handleSwiperAutoplayResume,
    setActiveSlideAutoplayProgress,
    setIsAutoplayPausedVisual,
  } = useGalleryPagination({
    swiperRef,
    enableAutoSwipe: shouldEnableAutoSwipe,
    paginationType,
    autoSwipeDelay,
    loop,
  });

  const { handleContainerClick } = useGalleryTapNavigation({ swiperRef });
  const [isCurrentSlideScrolled, setIsCurrentSlideScrolled] = useState(false);

  // Function to reset the timer and show buttons
  const resetNavigationTimer = useCallback(() => {
    if (navigationHideTimeoutRef.current) {
      clearTimeout(navigationHideTimeoutRef.current);
    }
    setShowNavigationButtons(true); // Always show buttons on interaction
    navigationHideTimeoutRef.current = setTimeout(() => {
      setShowNavigationButtons(false); // Hide after 3 seconds of inactivity
    }, 3000); // 3 seconds
  }, []);

  // Effect to start the timer on mount and clean up on unmount
  useEffect(() => {
    resetNavigationTimer(); // Start timer immediately on component mount
    return () => {
      if (navigationHideTimeoutRef.current) {
        clearTimeout(navigationHideTimeoutRef.current);
      }
    };
  }, [resetNavigationTimer]);

  // Handler for mouse movement to reset the timer
  const handleGalleryMouseMove = useCallback(() => {
    resetNavigationTimer();
  }, [resetNavigationTimer]);

  useEffect(() => {
    setIsSsr(false);
  }, []);

  // Track interest on mount and unmount
  useEffect(() => {
    const interestsToTrack = interests;
    if (interestsToTrack.length > 0) {
      // 1. Track 'click' when gallery is opened
      trackInterest(interestsToTrack, 'click');
    }

    // 2. Track 'time_spent' when user leaves the gallery
    return () => {
      if (interestsToTrack.length > 0) {
        const durationSeconds = (Date.now() - mountTime.current) / 1000;
        trackInterest(interestsToTrack, 'time_spent', durationSeconds);
      }
    };
    // We only want this to run once in mount time,
    // but we do need to consider our hard dep for interests.
  }, [interests]);

  // Swipe detection handlers
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    isSwiping.current = true;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isSwiping.current) {
      return;
    }

    touchEndY.current = e.touches[0].clientY;
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping.current) {
      return;
    }

    const deltaY = touchEndY.current - touchStartY.current;
    const deltaX = touchEndX.current - touchStartX.current;

    // Check if it's a vertical swipe (more vertical than horizontal)
    const isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);

    // Check if it's a swipe down (positive deltaY) and significant enough
    const isSwipeDown = isVerticalSwipe && deltaY > 50;

    if (isSwipeDown) {
      // Note: the ON_ARTICLE_SCROLLED_STATE_CHANGED flag is driven solely by the
      // actual content scroll position (see handleScrollContent). A swipe-down
      // gesture used to emit `isScrolledDown: true` here, but it raced with the
      // content settling at the top and produced a false->true flicker.
      if (onSwipeDown) {
        onSwipeDown();
      }
    }

    isSwiping.current = false;
  }, [onSwipeDown]);

  // Add touch event listeners to the swiper container. Depends on isSwiperReady
  // so it re-runs once onSwiper has populated swiperRef (on first render the
  // swiper instance — and thus its element — is not yet available).
  useEffect(() => {
    const swiperElement = swiperRef.current?.el;
    if (!swiperElement) {
      return;
    }

    swiperElement.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    swiperElement.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });
    swiperElement.addEventListener('touchend', handleTouchEnd, {
      passive: true,
    });

    return () => {
      swiperElement.removeEventListener('touchstart', handleTouchStart);
      swiperElement.removeEventListener('touchmove', handleTouchMove);
      swiperElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isSwiperReady]);

  const handleScrollContent = useCallback((isScrolledDown: boolean) => {
    if (isScrolledDown) {
      setIsCurrentSlideScrolled(true);
    } else {
      setIsCurrentSlideScrolled(false);
    }
  }, []);

  useEffect(() => {
    bridge.onEvent(BridgeEvent.ON_ARTICLE_SCROLLED_STATE_CHANGED, {
      isScrolledDown: isCurrentSlideScrolled,
    });
  }, [isCurrentSlideScrolled, bridge]);

  useEffect(() => {
    setIsCurrentSlideScrolled(false);
  }, [internalCurrentIndex]);

  useEffect(() => {
    if (isCurrentSlideScrolled && onSlideScrolled) {
      onSlideScrolled();
    }
  }, [isCurrentSlideScrolled, onSlideScrolled]);

  useEffect(() => {
    // disables nab button clicking from propagating to slider and causing double-click advance
    document.querySelectorAll('.swiper-button-prev').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.stopPropagation();
      });
    });
    document.querySelectorAll('.swiper-button-next').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.stopPropagation();
      });
    });
  }, []);

  const totalSlideCount = useMemo(() => {
    return items.length + 1;
  }, [items.length]);

  const slideItems = useMemo(() => {
    const slides = [...items];
    slides.push({ type: 'recirculation' } as any);
    return slides;
  }, [items]);

  const handleSlideChange = useCallback(
    (swiper: SwiperInstance) => {
      const newIndex = swiper.realIndex;
      const oldIdx = internalCurrentIndex;
      setInternalCurrentIndex(newIndex);
      if (onPageChange) {
        onPageChange(newIndex, slideItems[newIndex]);
      }
      if (paginationType === 'customWithProgress' && oldIdx !== newIndex) {
        resetAutoplayProgressForNewSlide();
      }
      if (newIndex === items.length) {
        if (!searchParams.has('recirculation')) {
          addRecircParam();
        }
      }

      // Track 'swipe' interaction
      if (interests.length > 0 && oldIdx !== newIndex) {
        trackInterest(interests, 'swipe');
      }
    },
    [
      onPageChange,
      slideItems,
      paginationType,
      resetAutoplayProgressForNewSlide,
      internalCurrentIndex,
      interests,
    ]
  );

  const handleCustomDotClick = (index: number) => {
    if (swiperRef.current) {
      if (loop) {
        swiperRef.current.slideToLoop(index);
      } else {
        swiperRef.current.slideTo(index);
      }
      if (paginationType === 'customWithProgress') {
        setActiveSlideAutoplayProgress(0);
        setIsAutoplayPausedVisual(
          swiperRef.current.autoplay?.running ? false : true
        );
      }
    }
  };

  if (isSsr && items.length === 0) {
    return (
      <div className={`gallery-container ${className} gallery-loading-ssr`}>
        {t('Loading...')}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className={`gallery-container ${className} gallery-empty`}>
        {t('No items to display.')}
      </div>
    );
  }

  const swiperModules = [A11y, EffectFade, EffectCoverflow];
  if (showArrows) {
    swiperModules.push(Navigation);
  }
  if (enableAutoSwipe) {
    swiperModules.push(Autoplay);
  }
  if (enableHashNavigation) {
    swiperModules.push(HashNavigation);
  }
  if (showPagination && paginationType !== 'customWithProgress') {
    swiperModules.push(SwiperNativePaginationModule);
  }

  const handlePlayPauseClick = () => {
    if (
      swiperRef?.current?.autoplay.running &&
      swiperRef?.current?.autoplay.paused
    ) {
      onPlayPauseClick?.('play');
      swiperRef?.current?.autoplay.resume();
    } else if (!swiperRef?.current?.autoplay.running) {
      onPlayPauseClick?.('play');
      swiperRef?.current?.autoplay.start();
    } else {
      onPlayPauseClick?.('pause');
      swiperRef?.current?.autoplay.pause();
    }
  };

  const handleBackClick = () => {
    window.history.back();
  };

  const handleNextSlide = () => {
    swiperRef.current?.slideNext();
  };

  const addRecircParam = () => {
    // add recirculation param to url
    const recircSearchParam = new URLSearchParams(searchParams);
    recircSearchParam.set('recirculation', 'true');
    navigate(
      {
        pathname: location.pathname,
        search: recircSearchParam.toString(),
        hash: location.hash,
      },
      { replace: true }
    );
  };

  return (
    <div
      onClick={handleContainerClick}
      onMouseMove={handleGalleryMouseMove} // Add mouse move listener to reset timer
      className={`gallery-container ${className} ${!showNavigationButtons ? 'hide-nav-buttons' : ''}`} // Conditionally add class
    >
      {showPagination &&
        paginationType === 'customWithProgress' &&
        items.length > 0 && (
          <GalleryPaginationIndicators
            count={totalSlideCount}
            currentIndex={internalCurrentIndex}
            activeSlideAutoplayProgress={activeSlideAutoplayProgress}
            isAutoplayPaused={isAutoplayPausedVisual}
            onDotClick={handleCustomDotClick}
          />
        )}
      <button
        type="button"
        className="play-pause-btn-container"
        aria-label={
          isAutoplayPausedVisual ? t('Play slideshow') : t('Pause slideshow')
        }
        onClick={handlePlayPauseClick}
      >
        <PlayIcon
          className={`playpause-svg ${isAutoplayPausedVisual ? 'visible' : 'hidden'}`}
          aria-hidden={!isAutoplayPausedVisual}
        />
        <PauseIcon
          className={`playpause-svg ${!isAutoplayPausedVisual ? 'visible' : 'hidden'}`}
          aria-hidden={isAutoplayPausedVisual}
        />
      </button>

      {showBackButton && (
        <button
          type="button"
          className="back-btn-container"
          aria-label={t('Back')}
          onClick={handleBackClick}
        >
          <BackIcon className={`backbtn-svg`} />
        </button>
      )}

      <Swiper
        modules={swiperModules}
        initialSlide={initialIndex}
        spaceBetween={0}
        slidesPerView={1}
        loop={loop}
        grabCursor={true}
        centeredSlides={true}
        navigation={
          showArrows
            ? {
                disabledClass: 'gallery-swiper-btn-disabled-class',
              }
            : false
        }
        pagination={
          showPagination && paginationType !== 'customWithProgress'
            ? {
                clickable: true,
                type: paginationType as 'bullets' | 'fraction' | 'progressbar',
              }
            : false
        }
        autoplay={
          shouldEnableAutoSwipe
            ? {
                delay: autoSwipeDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
                waitForTransition: true,
                stopOnLastSlide: true,
              }
            : false
        }
        hashNavigation={
          enableHashNavigation
            ? { watchState: true, replaceState: true }
            : false
        }
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setIsSwiperReady(true);
          setInternalCurrentIndex(swiper.realIndex);
          if (onPageChange) {
            onPageChange(swiper.realIndex, slideItems[swiper.realIndex]);
          }
        }}
        onSlideChange={handleSlideChange}
        onAutoplayStart={handleSwiperAutoplayStart}
        onAutoplayStop={handleSwiperAutoplayStop}
        onAutoplayPause={handleSwiperAutoplayPause}
        onAutoplayResume={handleSwiperAutoplayResume}
        className="swiper-instance"
      >
        {items.map((item, index) => {
          const shouldLoadImage =
            index >= internalCurrentIndex - 1 &&
            index <= internalCurrentIndex + 1;
          return (
            <SwiperSlide
              key={item.lookId + index.toString()}
              data-hash={enableHashNavigation ? index.toString() : undefined}
              className="gallery-slide"
            >
              <CoverPage
                isDark={theme === 'dark'}
                title={
                  items.indexOf(item) === 0
                    ? item.title[language] || item.title['en']
                    : undefined
                }
                description={item.summary[language] || item.summary['en']}
                image={shouldLoadImage ? item.largestImage().url : undefined}
                isActive={
                  internalCurrentIndex === index && !isAutoplayPausedVisual
                }
                imageData={item.largestImage()}
                kenBurnsParams={item.kenBurnsParams}
                doNotTrack={true}
                ownedBy={index === 0 ? lookbookOwnedBy : undefined}
                pageIndex={index}
                isIgnoreParagraphInDescription={false}
                lookId={item.lookId}
                categoryList={item.iabCategories}
                onScroll={handleScrollContent}
                adType={adType}
                enableAd={internalCurrentIndex === index}
                credit={item.credit ? getSSRTranslation(item.credit, i18n) : ''}
                lbtype={item.lbtype}
                onNext={handleNextSlide}
                sourceLink={item.sourceLink}
              />
            </SwiperSlide>
          );
        })}
        <SwiperSlide
          data-hash={enableHashNavigation ? items.length.toString() : undefined}
        >
          <RecirculationPage
            lookbookId={lookbookId}
            enabled={internalCurrentIndex >= items.length - 1}
            onScroll={handleScrollContent}
          />
        </SwiperSlide>
      </Swiper>
    </div>
  );
};
